"use client";

import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, Star, Zap, Package, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";

export default function IGETProductNew() {
  // Load IGET product data
  const { data: igetProducts = [], isLoading } = trpc.products.getByBrand.useQuery({
    brand: "IGET",
    limit: 10,
  });

  // Calculate summary statistics
  const stats = useMemo(() => {
    if (igetProducts.length === 0) {
      return {
        avgRating: 0,
        totalReviews: 0,
        totalProducts: 0,
      };
    }

    const ratings = igetProducts
      .map((p) => (typeof p.rating === "string" ? parseFloat(p.rating) : p.rating || 0))
      .filter((r) => r > 0);

    const avgRating = ratings.length > 0 ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : "0";
    const totalReviews = igetProducts.reduce((sum, p) => sum + (p.reviewCount || 0), 0);

    return {
      avgRating: parseFloat(avgRating),
      totalReviews,
      totalProducts: igetProducts.length,
    };
  }, [igetProducts]);

  const renderStars = (rating: number | string | null) => {
    const r = typeof rating === "string" ? parseFloat(rating) : rating || 0;
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={16}
            className={i < Math.floor(r) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
          />
        ))}
      </div>
    );
  };

  const formatPrice = (price: number | string) => {
    const p = typeof price === "string" ? parseFloat(price) : price;
    return `AUD $${p.toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="px-4 py-16 md:py-24 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="mb-4 bg-blue-50 text-blue-700 hover:bg-blue-100">IGET Vapes</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              IGET
              <br />
              <span className="text-blue-600">Diverse Flavors, Innovative Experience</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              IGET is one of Australia's most popular vape brands, renowned for its extensive flavor selection and
              innovative design. Featuring an open pod system with 100+ flavor options, IGET is perfect for users
              seeking variety and exploration. All products comply with Australian law and offer fast local delivery.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                Shop Now
              </Button>
              <Button size="lg" variant="outline">
                Learn More
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6">
              <div>
                <div className="text-3xl font-bold text-gray-900">{stats.avgRating.toFixed(1)}/5</div>
                <div className="text-sm text-gray-600">Average Rating</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900">{stats.totalReviews}+</div>
                <div className="text-sm text-gray-600">Customer Reviews</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900">{stats.totalProducts}</div>
                <div className="text-sm text-gray-600">Products Available</div>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-8 h-96 flex items-center justify-center">
            {igetProducts.length > 0 && igetProducts[0].imageUrl ? (
              <img
                src={igetProducts[0].imageUrl}
                alt="IGET Product"
                className="h-full w-full object-cover rounded-lg"
              />
            ) : (
              <div className="text-center">
                <div className="text-6xl mb-4">🌈</div>
                <p className="text-gray-600">IGET Product Showcase</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Products Table Section */}
      <section className="px-4 py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">IGET Product Lineup</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Product Name</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">SKU</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Price</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Rating</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Reviews</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Status</th>
                </tr>
              </thead>
              <tbody>
                {igetProducts.map((product) => (
                  <tr key={product.id} className="border-b border-gray-200 hover:bg-white">
                    <td className="py-3 px-4 text-gray-900 font-medium">{product.name}</td>
                    <td className="py-3 px-4 text-gray-700">{product.sku}</td>
                    <td className="py-3 px-4 text-gray-900 font-semibold">{formatPrice(product.price)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {renderStars(product.rating)}
                        <span className="text-sm text-gray-600">
                          {typeof product.rating === "string" ? parseFloat(product.rating).toFixed(1) : product.rating}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-700">{product.reviewCount || 0}</td>
                    <td className="py-3 px-4">
                      <Badge
                        className={
                          product.status === "in_stock"
                            ? "bg-green-50 text-green-700"
                            : product.status === "low_stock"
                              ? "bg-yellow-50 text-yellow-700"
                              : "bg-red-50 text-red-700"
                        }
                      >
                        {product.status === "in_stock"
                          ? "In Stock"
                          : product.status === "low_stock"
                            ? "Low Stock"
                            : "Sold Out"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 py-16 max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Why Choose IGET?</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Zap className="text-blue-600" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">100+ Flavors</h3>
            </div>
            <p className="text-gray-600">
              Extensive flavor selection with open pod system for customization and variety. From fruity to minty,
              find your perfect taste.
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <Package className="text-green-600" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Fast Delivery</h3>
            </div>
            <p className="text-gray-600">
              Local Australian warehouse ensures quick delivery. Get your IGET products within 3-7 business days with
              secure packaging.
            </p>
          </Card>

          <Card className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <Check className="text-purple-600" size={24} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Legal & Safe</h3>
            </div>
            <p className="text-gray-600">
              All products comply with Australian regulations. Age-verified checkout, secure encryption, and 30-day
              satisfaction guarantee.
            </p>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="px-4 py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Frequently Asked Questions</h2>
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="technical">Technical</TabsTrigger>
              <TabsTrigger value="legal">Legal</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-4">
              <Card className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">What makes IGET different from other brands?</h3>
                <p className="text-gray-600">
                  IGET stands out with its open pod system offering 100+ flavor options, allowing users to customize
                  their vaping experience. Combined with reliable performance and fast local delivery, IGET is the
                  choice for flavor enthusiasts.
                </p>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">How long does delivery take?</h3>
                <p className="text-gray-600">
                  We offer fast local delivery from our Australian warehouse. Standard delivery takes 3-7 business
                  days. Express options may be available for selected areas.
                </p>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">Do you offer bulk discounts?</h3>
                <p className="text-gray-600">
                  Yes! We offer special pricing for bulk orders. Contact our sales team for quotes on orders of 10 or
                  more units.
                </p>
              </Card>
            </TabsContent>

            <TabsContent value="technical" className="space-y-4">
              <Card className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">What is the battery capacity of IGET products?</h3>
                <p className="text-gray-600">
                  Battery capacity varies by model. IGET ONE features a 1,900 mAh battery, while IGET BAR has 1,000
                  mAh. Check the product specifications for detailed information.
                </p>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">How many puffs can I expect?</h3>
                <p className="text-gray-600">
                  Puff count depends on the model and usage patterns. IGET ONE offers up to 12,000 puffs, while IGET
                  BAR provides around 3,500 puffs. Actual puff count may vary based on individual usage.
                </p>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">Are IGET products rechargeable?</h3>
                <p className="text-gray-600">
                  Yes, IGET ONE is rechargeable with a USB-C port, allowing for multiple uses. IGET BAR is
                  non-rechargeable for convenience.
                </p>
              </Card>
            </TabsContent>

            <TabsContent value="legal" className="space-y-4">
              <Card className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">Are IGET products legal in Australia?</h3>
                <p className="text-gray-600">
                  Yes, all our IGET products comply with Australian regulations. We ensure age verification (21+) and
                  follow all local laws regarding vape sales.
                </p>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">What is your return policy?</h3>
                <p className="text-gray-600">
                  We offer a 30-day satisfaction guarantee. If you're not satisfied with your purchase, contact us for
                  a return or refund within 30 days of purchase.
                </p>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">How do you verify age?</h3>
                <p className="text-gray-600">
                  We use industry-standard age verification at checkout. You must be 21+ to purchase IGET products.
                  All orders are verified before shipment.
                </p>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-16 max-w-6xl mx-auto">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Experience IGET?</h2>
          <p className="text-lg mb-8 opacity-90">
            Browse our full selection of IGET products and find your perfect flavor today.
          </p>
          <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
            Shop IGET Products Now
          </Button>
        </div>
      </section>
    </div>
  );
}
